import { apiClient, handleApiResponse, setAccessToken } from './api';
import type { AuthUser, LoginRequest, LoginResponse } from '@/types';

export async function login(request: LoginRequest): Promise<LoginResponse> {
  // Login endpoint returns LoginResponse directly (not wrapped in ApiResponse)
  // so we access response.data directly instead of using handleApiResponse
  try {
    const response = await apiClient.post<LoginResponse>('/auth/login', request);
    const loginResponse = response.data;

    if (loginResponse.success && loginResponse.accessToken) {
      // Pass rememberMe to persist token appropriately
      setAccessToken(loginResponse.accessToken, request.rememberMe);
    }

    return loginResponse;
  } catch (error: unknown) {
    // Axios throws for non-2xx responses - extract the friendly error from the response body
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as { response?: { data?: LoginResponse } };
      if (axiosError.response?.data?.error) {
        return { success: false, error: axiosError.response.data.error };
      }
    }
    return { success: false, error: 'Unable to connect. Please try again.' };
  }
}

export async function logout(): Promise<void> {
  try {
    await apiClient.post('/auth/logout');
  } finally {
    setAccessToken(null);
  }
}

export async function getCurrentUser(): Promise<AuthUser | null> {
  try {
    return await handleApiResponse<AuthUser>(apiClient.get('/auth/me'));
  } catch {
    return null;
  }
}

export async function refreshToken(): Promise<{ accessToken: string }> {
  const response = await handleApiResponse<{ accessToken: string }>(
    apiClient.post('/auth/refresh')
  );

  if (response.accessToken) {
    setAccessToken(response.accessToken);
  }

  return response;
}

export async function forgotPassword(email: string): Promise<void> {
  return handleApiResponse(apiClient.post('/auth/forgot-password', { email }));
}

export async function resetPassword(email: string, token: string, newPassword: string): Promise<void> {
  return handleApiResponse(apiClient.post('/auth/reset-password', { email, token, newPassword }));
}

export async function changePassword(currentPassword: string, newPassword: string, confirmPassword: string): Promise<void> {
  return handleApiResponse(apiClient.post('/members/change-password', { currentPassword, newPassword, confirmPassword }));
}

export interface ValidateInviteResponse {
  valid: boolean;
  email?: string;
  firstName?: string;
  error?: string;
}

export interface RegisterRequest {
  inviteId: string;
  password: string;
  confirmPassword: string;
}

export interface RegisterResponse {
  success: boolean;
  user?: AuthUser;
  accessToken?: string;
  refreshToken?: string;
  error?: string;
  /** If the member has a pending membership purchase, this contains the basket ID for checkout redirect */
  basketId?: string;
}

export async function validateInvite(inviteId: string): Promise<ValidateInviteResponse> {
  const response = await apiClient.get(`/auth/validate-invite/${inviteId}`);
  return response.data;
}

export async function register(request: RegisterRequest): Promise<RegisterResponse> {
  const response = await apiClient.post('/auth/register', request);

  if (response.data.success && response.data.accessToken) {
    setAccessToken(response.data.accessToken);
  }

  return response.data;
}
